import { fetchSession } from "../lib/api";
import type { SessionAccount } from "../lib/api";
import { bootstrapAccount, getSession, isAuthConfigured } from "../lib/supabaseAuth";
import type { Role } from "../types";
import { PENDING_SIGNUP_KEY, readStoredJson, storageRemove } from "./workspaceStorage";

type PendingSignup = {
  role?: Role;
  businessName?: string;
};

export type WorkspaceSessionResolution =
  | {
      status: "signed_in";
      account: SessionAccount;
      authNotice: null;
    }
  | {
      status: "signed_out";
      account: null;
      authNotice: null;
    }
  | {
      status: "needs_onboarding";
      account: null;
      authNotice: string;
    };

export function clearLegacySessionRole() {
  storageRemove("veleo.sessionRole");
}

function signedOut(): WorkspaceSessionResolution {
  clearLegacySessionRole();
  return {
    account: null,
    authNotice: null,
    status: "signed_out",
  };
}

function pendingSignup() {
  return readStoredJson<PendingSignup | null>(PENDING_SIGNUP_KEY, null);
}

async function finishPendingSignup() {
  const pending = pendingSignup();

  if (
    !pending?.businessName ||
    (pending.role !== "restaurant" && pending.role !== "supplier")
  ) {
    return null;
  }

  const created = await bootstrapAccount({
    orgName: pending.businessName,
    orgType: pending.role,
  });

  if (!created.ok) {
    return null;
  }

  storageRemove(PENDING_SIGNUP_KEY);
  const confirmed = await fetchSession();
  return confirmed.ok ? confirmed.account : null;
}

export async function resolveWorkspaceSession(): Promise<WorkspaceSessionResolution> {
  if (!isAuthConfigured() || !getSession()) {
    return signedOut();
  }

  const result = await fetchSession();

  if (result.ok) {
    return {
      account: result.account,
      authNotice: null,
      status: "signed_in",
    };
  }

  if (result.status === 403) {
    const account = await finishPendingSignup();

    if (account) {
      return {
        account,
        authNotice: null,
        status: "signed_in",
      };
    }

    return {
      account: null,
      authNotice: "Finish creating your Veleo workspace to continue.",
      status: "needs_onboarding",
    };
  }

  return signedOut();
}
